import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { useAuth } from "../../context/AuthContext";
import API from "../../services/api";
import { useNavigate } from "react-router-dom";
import ReservationCard from "./ReservationCard";
import { t } from "@lingui/macro";

const ReservationSearch = () => {
  const { authToken } = useAuth();
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    email: "",
    phoneNumber: "",
    startDate: "",
    endDate: "",
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selectedReservation, setSelectedReservation] = useState(null);

  const handleCloseCard = () => {
    setSelectedReservation(null);
  };

  const fetchReservations = async (page) => {
    if (!authToken) {
      console.error("No auth token found!");
      return;
    }

    try {
      const requestData = {};

      if (filters.email) requestData.email = filters.email;
      if (filters.phoneNumber) requestData.phoneNumber = filters.phoneNumber;
      if (filters.startDate) requestData.startDate = filters.startDate;
      if (filters.endDate) requestData.endDate = filters.endDate;

      requestData.page = page;
      requestData.size = 10;

      console.log("Request Data:", requestData);

      const response = await API.post("/api/reservations/search", requestData);
      console.log(response.data);

      if (response.data) {
        setReservations(response.data.content);
        setTotalPages(response.data.totalPages);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error("Error fetching reservations:", error.response?.data || error.message);
    }
  };

  useEffect(() => {
    fetchReservations(0);
  }, []);

  const handleSearch = () => {
    fetchReservations(0);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 0 && pageNumber < totalPages) {
      fetchReservations(pageNumber);
    }
  };

  const handleSearchChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedReservations = [...reservations].sort((a, b) => {
      if (key === "startDate" || key === "endDate") {
        return new Date(a[key]) - new Date(b[key]);
      }
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setReservations(sortedReservations);
  };

  const handleRowClick = async (reservationId) => {
    try {
      const response = await API.get(`/api/reservations/${reservationId}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      setSelectedReservation(response.data);
    } catch (error) {
      console.error("Error fetching reservation details:", error);
    }
  };

  const updateReservation = (updatedReservation) => {
    setReservations((prevReservations) =>
      prevReservations.map((reservation) =>
        reservation.id === updatedReservation.id ? updatedReservation : reservation
      )
    );
    setSelectedReservation(null);
  };

  const formatCancellationDate = (date) => {
    if (!date) return t`N/A`;
    const cancellationDate = new Date(date);
    return format(cancellationDate, "dd MMM yyyy 'at' HH:mm");
  };

  if (selectedReservation) {
    return (
      <ReservationCard
        reservation={selectedReservation}
        updateReservation={updateReservation}
        closeCard={handleCloseCard}
      />
    );
  }

  return (
    <div>
      <div>
        <input
          className="search-input"
          type="text"
          name="email"
          placeholder={t`Email`}
          value={filters.email}
          onChange={handleSearchChange}
        />
        <input
          className="search-input"
          type="text"
          name="phoneNumber"
          placeholder={t`Phone Number`}
          value={filters.phoneNumber}
          onChange={handleSearchChange}
        />
        <input
          className="search-input"
          type="date"
          name="startDate"
          value={filters.startDate}
          onChange={handleSearchChange}
        />
        <input
          className="search-input"
          type="date"
          name="endDate"
          value={filters.endDate}
          onChange={handleSearchChange}
        />
        <button className="add-new-btn" onClick={handleSearch}>
          {t`Search`}
        </button>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th className="sortable" onClick={() => handleSort("email")}>
              {t`Email`} {sortConfig.key === "email" && (sortConfig.direction === "asc" ? "🔼" : "🔽")}
            </th>
            <th className="sortable" onClick={() => handleSort("phoneNumber")}>
              {t`Phone Number`} {sortConfig.key === "phoneNumber" && (sortConfig.direction === "asc" ? "🔼" : "🔽")}
            </th>
            <th className="sortable" onClick={() => handleSort("startDate")}>
              {t`Start Date`} {sortConfig.key === "startDate" && (sortConfig.direction === "asc" ? "🔼" : "🔽")}
            </th>
            <th className="sortable" onClick={() => handleSort("endDate")}>
              {t`End Date`} {sortConfig.key === "endDate" && (sortConfig.direction === "asc" ? "🔼" : "🔽")}
            </th>
            <th>{t`Total Amount`}</th>
            <th>{t`Paid Amount`}</th>
            <th>{t`Remaining Amount`}</th>
            <th>{t`Status`}</th>
          </tr>
        </thead>
        <tbody>
          {reservations.length > 0 ? (
            reservations.map((reservation, index) => (
              <tr
                key={reservation.id}
                className={index % 2 === 0 ? "even-row" : "odd-row"}
                onClick={() => handleRowClick(reservation.id)}
              >
                <td>{reservation.email}</td>
                <td>{reservation.phoneNumber}</td>
                <td>{new Date(reservation.startDate).toLocaleDateString()}</td>
                <td>{new Date(reservation.endDate).toLocaleDateString()}</td>
                <td>{reservation.totalAmount}</td>
                <td>{reservation.paidAmount}</td>
                <td>{reservation.remainingAmount}</td>
                <td>
                  {reservation.status === "ACTIVE"
                    ? t`Active`
                    : reservation.status === "CANCELED" && reservation.cancellationDate
                    ? `${t`Canceled on`} ${formatCancellationDate(reservation.cancellationDate)}`
                    : reservation.status}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="9">{t`No reservations found.`}</td>
            </tr>
          )}
        </tbody>
      </table>

      {reservations.length > 0 && (
        <div className="pagination">
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0}>
            {t`Previous`}
          </button>
          <span>
            {t`Page`} {currentPage + 1} {t`of`} {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage + 1 === totalPages}
          >
            {t`Next`}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReservationSearch;
